import hasBuilderComponent from 'utils/builder/hasBuilderComponent';

import type { FooterVariant, HeaderVariant } from 'types/global';

type PageChromeData =
  | {
      blocks?: unknown[];
      topNavVariant?: HeaderVariant;
      footerVariant?: FooterVariant;
    }
  | null
  | undefined;

export type PageChrome = {
  headerVariant: HeaderVariant;
  footerVariant: FooterVariant;
};

/**
 * Derives whether the global header (plus promo bar) and footer should be
 * hidden for a given page or A/B variation.
 *
 * Immersive layouts (the Free Trial FTUX flow, the dashboard backdrop) force
 * both to 'hidden'; otherwise the editor-controlled topNavVariant/footerVariant
 * fields win, defaulting to 'default'.
 *
 * IMPORTANT: run this against the *resolved* A/B variation, not only the SSR
 * parent variation. An immersive layout can live in a child variation that the
 * client swaps in after hydration (e.g. /free-trial), in which case deriving
 * from the parent variation alone leaves the global nav, promo bar, and footer
 * mounted over the immersive page.
 */
const derivePageChrome = (data: PageChromeData): PageChrome => {
  const blocks = data?.blocks as Parameters<typeof hasBuilderComponent>[0];
  const isImmersivePage =
    hasBuilderComponent(blocks, 'FreeTrialFTUX') || hasBuilderComponent(blocks, 'DashboardBackdrop');

  return {
    headerVariant: isImmersivePage ? 'hidden' : (data?.topNavVariant ?? 'default'),
    footerVariant: isImmersivePage ? 'hidden' : (data?.footerVariant ?? 'default'),
  };
};

export default derivePageChrome;
